<template>
  <div class="companies-page">
    <div class="top-bar">
      <h1>Companies</h1>
      <div class="actions">
        <button class="btn" @click="openAddModal">Add Company</button>
        <button class="btn" @click="goStarredOrgs">Starred Companies</button>
        <button class="btn">Company Cleanup</button>
        <button class="btn">Export</button>
      </div>
    </div>

    <div class="search-row">
      <input
        v-model.trim="search"
        class="search-input"
        placeholder="Search companies…"
        @input="loadCompanies"
      />
    </div>

    <p v-if="loading" class="loading-message">
      🐢 Whoa dude, there are THOUSANDS of companies! Loading...
    </p>
    <p v-else-if="error" class="error">{{ error }}</p>
    <template v-else>
      <p class="count-row">Showing {{ companies.length }} of {{ total }}</p>
      <div class="table-wrap" @scroll="onScroll">
      <table class="table companies-table">
        <thead>
          <tr>
            <th>★</th>
            <th>Company Name</th>
            <th>Contact(s)</th>
            <th>Category</th>
            <th>Website</th>
            <th>Lead Status</th>
            <th>Life Cycle Status</th>
            <th>Lists</th>
            <th>Tags</th>
            <th>Date of last order</th>
            <th>Total Sales</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in companies" :key="c.company">
            <td>
              <button class="star-btn" @click="toggleStar(c)">
                {{ c.starred ? '★' : '☆' }}
              </button>
            </td>
            <td>
              <button type="button" class="company-link" @click="viewCompanyContacts(c)">
                {{ c.company }}
              </button>
            </td>
            <td>
              <button type="button" class="contacts-link" @click="viewCompanyContacts(c)">
                {{ c.contacts_count }}
              </button>
              <button type="button" class="add-people-btn" title="Add contacts to this company" @click="openAddPeople(c)">
                +
              </button>
            </td>
            <td>
              <select
                v-model="c.category"
                @change="updateCategory(c)"
                class="category-select"
              >
                <option value="">—</option>
                <option v-for="opt in categoryOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </td>
            <td>
              <a v-if="c.website" class="website-link" :href="formatWebsite(c.website)" target="_blank" rel="noopener">
                {{ c.website }}
              </a>
              <span v-else>—</span>
            </td>
            <td>
              <select
                v-model="c.lead_status"
                @change="updateLeadStatus(c)"
                class="lead-status-select"
                :class="leadStatusClass(c.lead_status)"
              >
                <option value="">—</option>
                <option v-for="opt in leadStatusOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </td>
            <td>
              <select
                v-model="c.life_cycle_status"
                @change="updateLifeCycle(c)"
                class="life-cycle-select"
              >
                <option value="">—</option>
                <option v-for="opt in lifeCycleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </td>
            <td>
              <span v-for="l in c.lists" :key="l" class="pill">{{ l }}</span>
              <span v-if="!c.lists.length">—</span>
            </td>
            <td>
              <span v-for="t in c.tags" :key="t" class="pill">{{ t }}</span>
              <span v-if="!c.tags.length">—</span>
            </td>
            <td>{{ formatDate(c.last_order_date) }}</td>
            <td>{{ c.total_sales ? `$${c.total_sales.toFixed(2)}` : '—' }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="loadingMore" class="loading-message">Loading more…</div>
      </div>
    </template>

    <div v-if="showAddModal" class="modal-backdrop" @click.self="closeAddModal">
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <h2>Add Company</h2>
            <p>Create a company record with table fields.</p>
          </div>
          <button class="icon-btn" @click="closeAddModal">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <div class="form-field">
              <label>Company Name</label>
              <input v-model.trim="addForm.company" placeholder="Acme Education" />
            </div>
            <div class="form-field">
              <label>Website</label>
              <input v-model.trim="addForm.website" placeholder="acme.com" />
            </div>
            <div class="form-field">
              <label>Contacts Count</label>
              <input v-model.number="addForm.contacts_count" type="number" min="0" />
            </div>
            <div class="form-field">
              <label>Category</label>
              <select v-model="addForm.category">
                <option value="">—</option>
                <option v-for="opt in categoryOptions" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </div>
            <div class="form-field">
              <label>Lead Status</label>
              <select v-model="addForm.lead_status">
                <option value="">—</option>
                <option v-for="opt in leadStatusOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="form-field">
              <label>Life Cycle Status</label>
              <select v-model="addForm.life_cycle_status">
                <option value="">—</option>
                <option v-for="opt in lifeCycleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-field">
              <label>Lists (comma‑separated)</label>
              <input v-model.trim="addForm.lists" placeholder="List A, List B" />
            </div>
            <div class="form-field">
              <label>Tags (comma‑separated)</label>
              <input v-model.trim="addForm.tags" placeholder="ISTE2025, Educator" />
            </div>
            <div class="form-field">
              <label>Date of last order</label>
              <input v-model="addForm.last_order_date" type="date" />
            </div>
            <div class="form-field">
              <label>Total Sales</label>
              <input v-model.number="addForm.total_sales" type="number" min="0" step="0.01" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="closeAddModal">Cancel</button>
          <button class="btn" :disabled="savingAdd" @click="submitAddCompany">
            {{ savingAdd ? 'Saving…' : 'Add Company' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add people to existing company -->
    <div v-if="showPeopleModal" class="modal-backdrop" @click.self="closePeopleModal">
      <div class="modal-card people-modal">
        <div class="modal-header">
          <div>
            <h2>Add contacts</h2>
            <p>Assign people to <strong>{{ peopleCompany }}</strong></p>
          </div>
          <button class="icon-btn" @click="closePeopleModal">✕</button>
        </div>
        <div class="modal-body">
          <input
            v-model.trim="peopleSearch"
            class="people-search"
            placeholder="Search contacts by name or email…"
            @input="onPeopleSearch"
          />
          <p v-if="peopleError" class="error">{{ peopleError }}</p>
          <p v-else-if="peopleLoading" class="loading-message">Searching…</p>
          <div v-else class="people-list">
            <label
              v-for="c in peopleResults"
              :key="c.id"
              class="people-row"
              :class="{ selected: peopleSelected.has(c.id) }"
            >
              <input
                type="checkbox"
                :checked="peopleSelected.has(c.id)"
                @change="togglePeople(c.id, $event.target.checked)"
              />
              <span class="people-name">{{ displayContact(c) }}</span>
              <span class="people-email">{{ c.main_email }}</span>
              <span class="people-company" :title="c.company || ''">{{ c.company || '—' }}</span>
            </label>
            <p v-if="!peopleResults.length && peopleSearch.length >= 2" class="empty-people">
              No matching contacts.
            </p>
            <p v-else-if="peopleSearch.length < 2" class="empty-people">
              Type at least 2 characters to search.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <span class="people-count">{{ peopleSelected.size }} selected</span>
          <button class="btn ghost" @click="closePeopleModal">Cancel</button>
          <button
            class="btn"
            :disabled="savingPeople || !peopleSelected.size"
            @click="submitPeople"
          >
            {{ savingPeople ? 'Saving…' : 'Add to company' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomersService from '@/services/CustomersService'
import { LIFE_CYCLE_OPTIONS, LEAD_STATUS_OPTIONS } from '@/data/contactStatuses'

function debounce(fn, ms) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}

export default {
  name: 'CompaniesPage',
  data() {
    return {
      companies: [],
      loading: true,
      error: '',
      search: '',
      page: 1,
      perPage: 100,
      total: 0,
      loadingMore: false,
      showAddModal: false,
      savingAdd: false,
      addForm: {
        company: '',
        website: '',
        contacts_count: 0,
        category: '',
        lead_status: '',
        life_cycle_status: '',
        lists: '',
        tags: '',
        last_order_date: '',
        total_sales: null
      },
      showPeopleModal: false,
      peopleCompany: '',
      peopleSearch: '',
      peopleResults: [],
      peopleSelected: new Set(),
      peopleLoading: false,
      peopleError: '',
      savingPeople: false,
      onPeopleSearch: null,
      lifeCycleOptions: LIFE_CYCLE_OPTIONS,
      leadStatusOptions: LEAD_STATUS_OPTIONS,
      categoryOptions: [
        'Public school',
        'Private school',
        'Library',
        'Museum',
        'College/University',
        'Edu Distributor',
        'Retail distributor',
        'Craft Reseller',
        'Edu Reseller',
        'Other Reseller',
        'Non-profit',
        'Other'
      ]
    }
  },
  created() {
    this.onPeopleSearch = debounce(this.fetchPeople, 280)
    this.loadCompanies()
  },
  methods: {
    goStarredOrgs() {
      this.$router.push({ path: '/starred', query: { tab: 'orgs' } })
    },
    displayContact(c) {
      return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.main_email || `#${c.id}`
    },
    viewCompanyContacts(company) {
      this.$router.push({
        name: 'CompanyDetail',
        query: { name: company.company }
      })
    },
    openAddPeople(company) {
      this.peopleCompany = company.company
      this.peopleSearch = ''
      this.peopleResults = []
      this.peopleSelected = new Set()
      this.peopleError = ''
      this.showPeopleModal = true
    },
    closePeopleModal() {
      this.showPeopleModal = false
      this.peopleCompany = ''
      this.peopleSearch = ''
      this.peopleResults = []
      this.peopleSelected = new Set()
      this.peopleError = ''
    },
    togglePeople(id, checked) {
      const next = new Set(this.peopleSelected)
      if (checked) next.add(id)
      else next.delete(id)
      this.peopleSelected = next
    },
    async fetchPeople() {
      const q = this.peopleSearch.trim()
      if (q.length < 2) {
        this.peopleResults = []
        return
      }
      this.peopleLoading = true
      this.peopleError = ''
      try {
        const { data } = await CustomersService.list({ q, page: 1, per_page: 40 })
        this.peopleResults = data.customers || []
      } catch {
        this.peopleError = 'Could not search contacts.'
        this.peopleResults = []
      } finally {
        this.peopleLoading = false
      }
    },
    async submitPeople() {
      if (!this.peopleSelected.size || !this.peopleCompany) return
      this.savingPeople = true
      this.peopleError = ''
      try {
        const { data } = await CustomersService.addCompanyContacts(
          this.peopleCompany,
          Array.from(this.peopleSelected)
        )
        const row = this.companies.find((c) => c.company === this.peopleCompany)
        if (row && data.contacts_count != null) row.contacts_count = data.contacts_count
        this.closePeopleModal()
        this.loadCompanies(true)
      } catch {
        this.peopleError = 'Could not add contacts to company.'
      } finally {
        this.savingPeople = false
      }
    },
    loadCompanies(reset = true) {
      if (reset) {
        this.page = 1
        this.companies = []
      }
      this.loading = reset
      this.loadingMore = !reset
      this.error = ''
      CustomersService.listCompanies({ q: this.search, page: this.page, per_page: this.perPage })
        .then(({ data }) => {
          const list = data.companies || []
          this.total = data.total || list.length
          this.companies = reset ? list : [...this.companies, ...list]
          this.page += 1
        })
        .catch(() => {
          this.error = 'Could not load companies.'
        })
        .finally(() => {
          this.loading = false
          this.loadingMore = false
        })
    },
    onScroll(e) {
      const el = e.target
      if (this.loadingMore || this.companies.length >= this.total) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.loadCompanies(false)
      }
    },
    openAddModal() {
      this.resetAddForm()
      this.showAddModal = true
    },
    closeAddModal() {
      this.showAddModal = false
    },
    resetAddForm() {
      this.addForm = {
        company: '',
        website: '',
        contacts_count: 0,
        category: '',
        lead_status: '',
        life_cycle_status: '',
        lists: '',
        tags: '',
        last_order_date: '',
        total_sales: null
      }
    },
    submitAddCompany() {
      const payload = {
        company: this.addForm.company,
        website: this.addForm.website,
        contacts_count: this.addForm.contacts_count,
        category: this.addForm.category,
        lead_status: this.addForm.lead_status,
        life_cycle_status: this.addForm.life_cycle_status,
        lists: this.parseCommaList(this.addForm.lists),
        tags: this.parseCommaList(this.addForm.tags),
        last_order_date: this.addForm.last_order_date,
        total_sales: this.addForm.total_sales
      }
      this.savingAdd = true
      CustomersService.createCompany(payload)
        .then(() => {
          this.closeAddModal()
          this.loadCompanies(true)
        })
        .catch(() => {
          this.error = 'Could not add company.'
        })
        .finally(() => {
          this.savingAdd = false
        })
    },
    parseCommaList(value) {
      if (!value) return []
      return value.split(',').map((t) => t.trim()).filter(Boolean)
    },
    toggleStar(company) {
      CustomersService.toggleCompanyStar(company.company)
        .then(({ data }) => {
          company.starred = data.starred
        })
        .catch(() => {
          this.error = 'Could not update star.'
        })
    },
    updateLifeCycle(company) {
      CustomersService.updateCompanyProfile(company.company, {
        life_cycle_status: company.life_cycle_status
      })
        .catch(() => {
          this.error = 'Could not update life cycle status.'
        })
    },
    updateLeadStatus(company) {
      CustomersService.updateCompanyProfile(company.company, {
        lead_status: company.lead_status
      })
        .catch(() => {
          this.error = 'Could not update lead status.'
        })
    },
    updateCategory(company) {
      CustomersService.updateCompanyProfile(company.company, {
        category: company.category
      })
        .catch(() => {
          this.error = 'Could not update category.'
        })
    },
    leadStatusClass(value) {
      switch (value) {
        case 'new': return 'lead-blue'
        case 'in_progress': return 'lead-yellow'
        case 'waiting_blocked': return 'lead-red'
        case 'completed': return 'lead-green'
        case 'do_not_contact': return 'lead-gray'
        default: return ''
      }
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    },
    formatWebsite(value) {
      const raw = (value || '').toString().trim()
      if (!raw) return '#'
      if (raw.startsWith('http://') || raw.startsWith('https://')) return raw
      return `https://${raw}`
    }
  }
}
</script>

<style scoped>
.companies-page {
  padding: 0.5rem;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}
.top-bar {
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:1rem;
}
.actions { display:flex; gap:.5rem; flex-wrap:wrap; }
.count-row {
  margin: 0.25rem 0 0.5rem;
  color: #cbd5e0;
  font-size: 0.9rem;
}
.search-row {
  margin: 0.5rem 0 1rem;
}
.search-input {
  width: 100%;
  max-width: 360px;
  padding: 0.5rem 0.8rem;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.3);
  background: transparent;
  color: #fff;
}
.btn {
  background:#319795;
  border:none;
  border-radius:6px;
  padding:.45rem .8rem;
  color:#fff;
  font-weight:700;
  cursor:pointer;
}
.table-wrap {
  border:1px solid rgba(255,255,255,.12);
  border-radius:6px;
  overflow:auto;
  flex: 1 1 auto;
  min-height: 0;
}
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 8, 18, 0.75);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal-card {
  width: min(980px, 92vw);
  background: linear-gradient(145deg, rgba(26,32,44,0.95), rgba(13,18,30,0.95));
  border: 1px solid rgba(148,163,184,0.3);
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
  color: #e2e8f0;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.4rem 0.6rem;
}
.modal-header h2 {
  margin: 0;
}
.modal-header p {
  margin: 0.25rem 0 0;
  color: #a0aec0;
  font-size: 0.9rem;
}
.icon-btn {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.2);
  color: #e2e8f0;
  border-radius: 999px;
  width: 32px;
  height: 32px;
  cursor: pointer;
}
.modal-body {
  padding: 0.8rem 1.4rem 1rem;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem 1rem;
}
.form-field label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  color: #cbd5e0;
}
.form-field input,
.form-field select {
  width: 100%;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(9, 11, 22, 0.6);
  color: #e2e8f0;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 0 1.4rem 1.2rem;
}
.btn.ghost {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.25);
  color: #e2e8f0;
}
.companies-table th,
.companies-table td {
  padding:.6rem .75rem;
  border-bottom:1px solid rgba(255,255,255,.08);
  text-align:left;
  color:#fff;
}
.star-btn {
  background: none;
  border: none;
  color: #f6e05e;
  cursor: pointer;
  font-size: 1.1rem;
}
.company-link,
.contacts-link {
  background: none;
  border: none;
  color: #fbd38d;
  cursor: pointer;
  padding: 0;
  text-align: left;
  text-decoration: underline;
  font: inherit;
}
.company-link:hover,
.contacts-link:hover { color: #fff; }
.add-people-btn {
  margin-left: 0.4rem;
  background: rgba(128, 90, 213, 0.25);
  border: 1px solid rgba(128, 90, 213, 0.55);
  color: #e9d5ff;
  border-radius: 999px;
  width: 1.5rem;
  height: 1.5rem;
  line-height: 1;
  cursor: pointer;
  font-weight: 700;
  padding: 0;
}
.add-people-btn:hover {
  background: rgba(128, 90, 213, 0.45);
  color: #fff;
}
.people-modal { max-width: 640px; }
.people-search {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(9, 11, 22, 0.6);
  color: #e2e8f0;
  margin-bottom: 0.75rem;
}
.people-list {
  max-height: 42vh;
  overflow-y: auto;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 8px;
}
.people-row {
  display: grid;
  grid-template-columns: auto 1fr auto minmax(0, 8rem);
  gap: 0.5rem;
  align-items: center;
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid rgba(255,255,255,.06);
  cursor: pointer;
}
.people-row.selected { background: rgba(128, 90, 213, 0.15); }
.people-name { font-weight: 600; color: #fff; }
.people-email { color: #cbd5e0; font-size: 0.85rem; }
.people-company {
  color: #a0aec0;
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.empty-people { color: #a0aec0; padding: 1rem; margin: 0; }
.people-count {
  margin-right: auto;
  color: #a7f3d0;
  font-size: 0.9rem;
}
.life-cycle-select {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  border-radius: 6px;
  padding: 0.25rem 0.4rem;
}
.lead-status-select {
  border-radius: 6px;
  padding: 0.25rem 0.4rem;
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  background: rgba(255,255,255,0.05);
}
.category-select {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  border-radius: 6px;
  padding: 0.25rem 0.4rem;
}
.website-link {
  color: #9ae6b4;
  text-decoration: underline;
}
.lead-blue { background: rgba(66,153,225,0.25); }
.lead-yellow { background: rgba(236,201,75,0.25); color: #fff; }
.lead-red { background: rgba(229,62,62,0.25); }
.lead-green { background: rgba(72,187,120,0.25); }
.lead-gray { background: rgba(160,174,192,0.25); }
.pill {
  display:inline-block;
  padding:2px 8px;
  margin:0 4px 4px 0;
  border-radius:999px;
  background:rgba(255,255,255,.15);
  color:#fff;
  font-size:.85em;
  border:1px solid rgba(255,255,255,.25);
}
.company-name {
  min-width: 280px;
  max-width: 480px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.loading-message { font-size:1.1rem; font-style:italic; color:#ffa; margin:1rem 0 }
.error { color:#feb2b2; margin:1rem 0 }
</style>
